import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COMPANY } from "../lib/data";
import { useSettings } from "../lib/settings";
import { trackEvent } from "../lib/analytics";

const ABOUT_LINKS = [
  { to: "/about/team", label: "Team", blurb: "Purpose, Values & Leadership" },
  { to: "/about/timeline", label: "Timeline", blurb: "Five Decades, in Milestones" },
  { to: "/about/gallery", label: "Gallery", blurb: "Yards, Dispatch & Operations" },
];

const TOP_LINKS = [
  { to: "/", label: "Home", exact: true },
  { type: "about" },
  { to: "/products", label: "Products & Services" },
  { to: "/price", label: "Price" },
  { to: "/blog", label: "Blog" },
];

export const Navbar = () => {
  const { logoUrl } = useSettings();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const aboutActive = pathname.startsWith("/about");

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-black/5 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_-12px_rgba(28,28,30,0.12)]"
          : "border-transparent bg-[#f7f7f5]/70 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-3.5 lg:px-10">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img
            src={logoUrl}
            alt="Baheti Steel Traders"
            className="h-14 w-14 object-contain"
            data-testid="navbar-logo-img"
          />
          <div className="leading-tight">
            <div className="font-display text-[14px] font-extrabold uppercase tracking-tight text-[#1c1c1e]">
              Baheti Steel Traders LLP
            </div>
            <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-[#8b8b8f]">
              Since {COMPANY.foundedYear}
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {TOP_LINKS.map((l, i) => {
            if (l.type === "about") {
              return (
                <div
                  key="about"
                  className="relative"
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                >
                  <button
                    data-testid="nav-about"
                    className={`inline-flex items-center gap-1 px-4 py-2.5 text-[13px] font-medium tracking-wide transition-all duration-300 ${
                      aboutActive ? "text-[#e85a1c]" : "text-[#4a4a4e] hover:text-[#1c1c1e]"
                    }`}
                  >
                    About
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-300 ${aboutOpen ? "rotate-180" : ""}`}
                    />
                    <span
                      className={`absolute -bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 bg-[#e85a1c] transition-all duration-300 ${
                        aboutActive ? "w-6" : "w-0"
                      }`}
                    />
                  </button>

                  {/* Dropdown */}
                  <div
                    data-testid="about-dropdown"
                    className={`absolute left-1/2 top-full -translate-x-1/2 pt-3 transition-all duration-300 ${
                      aboutOpen
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none -translate-y-1 opacity-0"
                    }`}
                  >
                    <div className="w-[320px] overflow-hidden rounded-2xl border border-black/5 bg-white shadow-lux-hover">
                      <div className="border-b border-black/5 bg-[#f7f7f5] px-5 py-3">
                        <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-[#e85a1c]">
                          / About us
                        </div>
                        <div className="mt-0.5 font-display-med text-sm uppercase tracking-tight text-[#1c1c1e]">
                          The story behind the steel
                        </div>
                      </div>
                      <div className="p-2">
                        {ABOUT_LINKS.map((al) => (
                          <Link
                            key={al.to}
                            to={al.to}
                            data-testid={`nav-about-${al.label.toLowerCase()}`}
                            onClick={() => setAboutOpen(false)}
                            className="group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-[#fff1e8]"
                          >
                            <div>
                              <div className="text-sm font-semibold text-[#1c1c1e] group-hover:text-[#e85a1c]">
                                {al.label}
                              </div>
                              <div className="mt-0.5 text-[11px] text-[#8b8b8f]">
                                {al.blurb}
                              </div>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#bababd] group-hover:text-[#e85a1c]">
                              →
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2.5 text-[13px] font-medium tracking-wide transition-all duration-300 ${
                    isActive ? "text-[#e85a1c]" : "text-[#4a4a4e] hover:text-[#1c1c1e]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 bg-[#e85a1c] transition-all duration-300 ${
                        isActive ? "w-6" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            );
          })}
          <a
            href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`}
            data-testid="navbar-call-btn"
            onClick={() => trackEvent("phone_click", { source: "navbar_desktop", phone_number: COMPANY.phone })}
            className="ml-3 inline-flex items-center gap-2 rounded-full bg-[#1c1c1e] px-5 py-2.5 text-xs font-semibold tracking-wide text-white transition-all duration-300 hover:bg-[#e85a1c] hover:shadow-orange"
          >
            <Phone size={13} />
            {COMPANY.phone}
          </a>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-black/10 bg-white lg:hidden"
          aria-label="menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-black/5 bg-white lg:hidden">
          <div className="flex flex-col px-6 py-4">
            <NavLink
              to="/"
              end
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `border-b border-black/5 py-3 text-sm font-medium ${
                  isActive ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                }`
              }
            >
              Home
            </NavLink>
            {/* Mobile About accordion */}
            <div className="border-b border-black/5">
              <button
                data-testid="mobile-about-toggle"
                onClick={() => setMobileAboutOpen((v) => !v)}
                className={`flex w-full items-center justify-between py-3 text-sm font-medium ${
                  aboutActive ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                }`}
              >
                About
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${mobileAboutOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`grid overflow-hidden transition-all duration-300 ${
                  mobileAboutOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="pb-3">
                    {ABOUT_LINKS.map((al) => (
                      <Link
                        key={al.to}
                        to={al.to}
                        onClick={() => {
                          setOpen(false);
                          setMobileAboutOpen(false);
                        }}
                        className="flex items-center justify-between border-l-2 border-[#e85a1c]/30 px-3 py-2.5 text-[13px] text-[#4a4a4e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
                      >
                        {al.label}
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#bababd]">→</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <NavLink
              to="/price"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `border-b border-black/5 py-3 text-sm font-medium ${
                  isActive ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                }`
              }
            >
              Price
            </NavLink>
            <NavLink
              to="/products"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `border-b border-black/5 py-3 text-sm font-medium ${
                  isActive ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                }`
              }
            >
              Products &amp; Services
            </NavLink>
            <NavLink
              to="/blog"
              data-testid="mobile-nav-blog"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `border-b border-black/5 py-3 text-sm font-medium ${
                  isActive ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                }`
              }
            >
              Blog
            </NavLink>
            <a
              href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`}
              onClick={() => trackEvent("phone_click", { source: "navbar_mobile", phone_number: COMPANY.phone })}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#1c1c1e] py-3 text-sm font-semibold text-white"
            >
              <Phone size={14} />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
